// ============================================================
// JIRA API FUNCTIONS
// High-level API functions for performance dashboard
// ============================================================

import { jiraClient } from './client'
import { ISSUE_FIELDS, EXTENDED_FIELDS, JQL_QUERIES } from './queries'
import type {
  JiraSearchResponse,
  JiraIssue,
  JiraIssueEnhanced,
  JiraUser,
  JiraWorklog,
  JiraWorklogResponse,
  JiraPersonSummary,
  JiraPersonDetail,
  JiraPeopleOverviewData,
  JiraUserPerformance,
  JiraTeamPerformance,
} from '@/types/jira'

const MAX_RESULTS = 100

// ============================================================
// ISSUE SEARCH FUNCTIONS
// ============================================================

export async function searchIssues(
  jql: string, 
  maxResults: number = MAX_RESULTS,
  fields: string = ISSUE_FIELDS
): Promise<JiraSearchResponse> {
  return jiraClient.search<JiraSearchResponse>({
    jql,
    maxResults,
    fields: fields.split(','),
  })
}

export async function searchIssuesWithTimeTracking(
  jql: string, 
  maxResults: number = MAX_RESULTS
): Promise<JiraSearchResponse> {
  return jiraClient.search<JiraSearchResponse>({
    jql,
    maxResults,
    fields: EXTENDED_FIELDS.split(','),
  })
}

export async function getIssue(issueKey: string): Promise<JiraIssue> {
  return jiraClient.getIssue<JiraIssue>(issueKey, ISSUE_FIELDS)
}

export async function getIssueEnhanced(issueKey: string): Promise<JiraIssueEnhanced> {
  return jiraClient.getIssue<JiraIssueEnhanced>(issueKey, EXTENDED_FIELDS, ['changelog'])
}

// ============================================================
// USER TASK FUNCTIONS
// ============================================================

export async function getOpenTasksByAssignee(accountId: string): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.openTasksByAssignee(accountId)
  const response = await searchIssues(jql)
  return response.issues
}

export async function getCompletedTasksByAssignee(
  accountId: string, 
  days: number = 30
): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.completedTasksByAssignee(accountId, days)
  const response = await searchIssues(jql)
  return response.issues
}

export async function getAllTasksByAssignee(
  accountId: string,
  days: number = 90
): Promise<JiraIssueEnhanced[]> {
  const jql = JQL_QUERIES.allTasksByAssignee(accountId, days)
  const response = await searchIssuesWithTimeTracking(jql)
  return response.issues as JiraIssueEnhanced[]
}

export async function getOverdueTasksByAssignee(accountId: string): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.overdueByAssignee(accountId)
  const response = await searchIssues(jql)
  return response.issues
}

// ============================================================
// GLOBAL TASK FUNCTIONS
// ============================================================

export async function getAllOpenTasks(): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.allOpenTasks()
  const response = await searchIssues(jql)
  return response.issues
}

export async function getAllRecentlyCompletedTasks(days: number = 30): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.allRecentlyCompleted(days)
  const response = await searchIssues(jql)
  return response.issues
}

export async function getAllRecentlyCreatedTasks(days: number = 30): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.allRecentlyCreated(days)
  const response = await searchIssues(jql)
  return response.issues
}

export async function getAllOverdueTasks(): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.allOverdue()
  const response = await searchIssues(jql)
  return response.issues
}

export async function getHighPriorityOpenTasks(): Promise<JiraIssue[]> {
  const jql = JQL_QUERIES.highPriorityOpen()
  const response = await searchIssues(jql)
  return response.issues
}

// ============================================================
// TIME TRACKING FUNCTIONS
// ============================================================

export async function getIssueWorklogs(issueKey: string): Promise<JiraWorklog[]> {
  const response = await jiraClient.getWorklogs<JiraWorklogResponse>(issueKey)
  return response.worklogs
}

export async function getTasksWithTimeLogged(): Promise<JiraIssueEnhanced[]> {
  const jql = JQL_QUERIES.withTimeLogged()
  const response = await searchIssuesWithTimeTracking(jql)
  return response.issues as JiraIssueEnhanced[]
}

export async function getUserTimeLogged(
  accountId: string
): Promise<{ totalSeconds: number; byProject: Record<string, number> }> {
  const jql = JQL_QUERIES.withTimeLoggedByAssignee(accountId)
  const response = await searchIssuesWithTimeTracking(jql)
  
  let totalSeconds = 0
  const byProject: Record<string, number> = {}
  
  for (const issue of response.issues as JiraIssueEnhanced[]) {
    const timeSpent = issue.fields.timespent || 0
    totalSeconds += timeSpent
    
    const projectKey = issue.fields.project.key
    byProject[projectKey] = (byProject[projectKey] || 0) + timeSpent
  }
  
  return { totalSeconds, byProject }
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function extractUniqueAssignees(issues: JiraIssue[]): JiraUser[] {
  const userMap = new Map<string, JiraUser>()
  
  issues.forEach(issue => {
    const assignee = issue.fields.assignee
    if (assignee && !userMap.has(assignee.accountId)) {
      userMap.set(assignee.accountId, assignee)
    }
  })
  
  return Array.from(userMap.values())
}

function calculateTasksByStatus(issues: JiraIssue[]): Record<string, number> {
  const statusCounts: Record<string, number> = {}
  
  issues.forEach(issue => {
    const statusName = issue.fields.status.name
    statusCounts[statusName] = (statusCounts[statusName] || 0) + 1
  })
  
  return statusCounts
}

function calculateTasksByProject(issues: JiraIssue[]): Record<string, number> {
  const projectCounts: Record<string, number> = {}
  
  issues.forEach(issue => {
    const projectKey = issue.fields.project.key
    projectCounts[projectKey] = (projectCounts[projectKey] || 0) + 1
  })
  
  return projectCounts
}

function calculateTasksByPriority(issues: JiraIssue[]): Record<string, number> {
  const priorityCounts: Record<string, number> = {}
  
  issues.forEach(issue => {
    const priorityName = issue.fields.priority?.name || 'None'
    priorityCounts[priorityName] = (priorityCounts[priorityName] || 0) + 1
  })
  
  return priorityCounts
}

function getTopProjects(
  issues: JiraIssue[], 
  limit: number = 3
): Array<{ key: string; name: string; count: number }> {
  const projectMap = new Map<string, { name: string; count: number }>()
  
  issues.forEach(issue => {
    const project = issue.fields.project
    const existing = projectMap.get(project.key)
    if (existing) {
      existing.count++
    } else {
      projectMap.set(project.key, { name: project.name, count: 1 })
    }
  })
  
  return Array.from(projectMap.entries())
    .map(([key, { name, count }]) => ({ key, name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

function calculateAvgResolutionTime(issues: JiraIssue[]): number {
  const resolvedIssues = issues.filter(i => i.fields.resolutiondate)
  if (resolvedIssues.length === 0) return 0
  
  const totalHours = resolvedIssues.reduce((sum, issue) => {
    const created = new Date(issue.fields.created).getTime()
    const resolved = new Date(issue.fields.resolutiondate!).getTime()
    return sum + (resolved - created) / (1000 * 60 * 60) // Hours
  }, 0)
  
  return Math.round(totalHours / resolvedIssues.length * 10) / 10
}

function calculateTotalTimeSpent(issues: JiraIssueEnhanced[]): number {
  return issues.reduce((sum, issue) => {
    return sum + (issue.fields.timespent || 0)
  }, 0) / 3600 // Convert seconds to hours
}

function countBlockedIssues(issues: JiraIssue[]): number {
  return issues.filter(issue => {
    return issue.fields.issuelinks?.some(link => 
      link.type.name === 'Blocks' && link.inwardIssue
    )
  }).length
}

// ============================================================
// PERSON SUMMARY & DETAIL
// ============================================================

export async function getPersonSummary(user: JiraUser): Promise<JiraPersonSummary> {
  const [openTasks, completedTasks] = await Promise.all([
    getOpenTasksByAssignee(user.accountId),
    getCompletedTasksByAssignee(user.accountId),
  ])

  const allUserTasks = [...openTasks, ...completedTasks]

  return {
    user,
    openTaskCount: openTasks.length,
    completedTaskCount: completedTasks.length,
    tasksByStatus: calculateTasksByStatus(allUserTasks),
    tasksByProject: calculateTasksByProject(allUserTasks),
    topProjects: getTopProjects(allUserTasks),
  }
}

export async function getPersonDetail(accountId: string): Promise<JiraPersonDetail> {
  const [openTasks, completedTasks] = await Promise.all([
    getOpenTasksByAssignee(accountId),
    getCompletedTasksByAssignee(accountId),
  ])

  if (openTasks.length === 0 && completedTasks.length === 0) {
    throw new Error('No tasks found for this user')
  }

  const firstTask = openTasks[0] || completedTasks[0]
  const user = firstTask.fields.assignee!

  const allUserTasks = [...openTasks, ...completedTasks]

  const blockedByIssues: JiraIssue[] = []
  const blockingIssues: JiraIssue[] = []

  openTasks.forEach(issue => {
    issue.fields.issuelinks?.forEach(link => {
      if (link.type.name === 'Blocks') {
        if (link.inwardIssue) {
          blockedByIssues.push(issue)
        }
        if (link.outwardIssue) {
          blockingIssues.push(issue)
        }
      }
    })
  })

  return {
    user,
    openTaskCount: openTasks.length,
    completedTaskCount: completedTasks.length,
    tasksByStatus: calculateTasksByStatus(allUserTasks),
    tasksByProject: calculateTasksByProject(allUserTasks),
    topProjects: getTopProjects(allUserTasks),
    openTasks,
    completedTasks,
    blockedByIssues,
    blockingIssues,
  }
}

// ============================================================
// ENHANCED PERSON PERFORMANCE
// ============================================================

export async function getPersonPerformance(accountId: string): Promise<JiraUserPerformance> {
  const [allTasks, overdueTasks] = await Promise.all([
    getAllTasksByAssignee(accountId, 90),
    getOverdueTasksByAssignee(accountId),
  ])

  if (allTasks.length === 0) {
    throw new Error('No tasks found for this user')
  }

  const firstTask = allTasks[0]
  const user = firstTask.fields.assignee!

  const completedTasks = allTasks.filter(t => t.fields.resolutiondate)
  const openTasks = allTasks.filter(t => !t.fields.resolutiondate)

  // Calculate time-based metrics
  const totalTimeSpent = calculateTotalTimeSpent(allTasks)
  const avgTimeSpentPerTask = allTasks.length > 0 
    ? totalTimeSpent / allTasks.length 
    : 0
  const avgResolutionTime = calculateAvgResolutionTime(allTasks)

  // Calculate velocity (tasks per week over last 30 days)
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  const recentlyCompleted = completedTasks.filter(t => 
    new Date(t.fields.resolutiondate!) >= thirtyDaysAgo
  )
  const velocityPerWeek = Math.round(recentlyCompleted.length / 4 * 10) / 10

  // Time spent by project
  const timeSpentByProject: Record<string, number> = {}
  allTasks.forEach(task => {
    const projectKey = task.fields.project.key
    const timeSpent = (task.fields.timespent || 0) / 3600
    timeSpentByProject[projectKey] = (timeSpentByProject[projectKey] || 0) + timeSpent
  })

  // Recent activity (last 14 days)
  const recentActivity = generateRecentActivity(allTasks, 14)

  return {
    user,
    metrics: {
      totalTasks: allTasks.length,
      completedTasks: completedTasks.length,
      openTasks: openTasks.length,
      completionRate: Math.round((completedTasks.length / allTasks.length) * 100),
      avgResolutionTime,
      avgTimeSpentPerTask: Math.round(avgTimeSpentPerTask * 10) / 10,
      totalTimeSpent: Math.round(totalTimeSpent * 10) / 10,
      velocityPerWeek,
      blockedTasks: countBlockedIssues(openTasks),
      overdueTaskRatio: openTasks.length > 0 
        ? Math.round((overdueTasks.length / openTasks.length) * 100) 
        : 0,
    },
    tasksByStatus: calculateTasksByStatus(allTasks),
    tasksByProject: calculateTasksByProject(allTasks),
    tasksByPriority: calculateTasksByPriority(allTasks),
    timeSpentByProject,
    recentActivity,
  }
}

function generateRecentActivity(
  tasks: JiraIssueEnhanced[], 
  days: number
): JiraUserPerformance['recentActivity'] {
  const activity: JiraUserPerformance['recentActivity'] = []
  
  for (let i = 0; i < days; i++) {
    const date = new Date()
    date.setDate(date.getDate() - i)
    const dateStr = date.toISOString().split('T')[0]
    
    const dayTasks = tasks.filter(t => {
      if (t.fields.resolutiondate) {
        return t.fields.resolutiondate.startsWith(dateStr)
      }
      return false
    })
    
    activity.push({
      date: dateStr,
      completedCount: dayTasks.length,
      timeSpent: dayTasks.reduce((sum, t) => sum + ((t.fields.timespent || 0) / 3600), 0),
    })
  }
  
  return activity.reverse()
}

// ============================================================
// TEAM OVERVIEW
// ============================================================

export async function getPeopleOverview(): Promise<JiraPeopleOverviewData> {
  const [openTasks, completedTasks] = await Promise.all([
    getAllOpenTasks(),
    getAllRecentlyCompletedTasks(),
  ])

  const allTasks = [...openTasks, ...completedTasks]
  const uniqueUsers = extractUniqueAssignees(allTasks)

  // Get summaries for all users in parallel (batched)
  const batchSize = 5
  const people: JiraPersonSummary[] = []
  
  for (let i = 0; i < uniqueUsers.length; i += batchSize) {
    const batch = uniqueUsers.slice(i, i + batchSize)
    const batchResults = await Promise.all(batch.map(user => getPersonSummary(user)))
    people.push(...batchResults)
  }

  // Sort by open task count
  people.sort((a, b) => b.openTaskCount - a.openTaskCount)

  return {
    people,
    totalOpenTasks: openTasks.length,
    totalCompletedTasks: completedTasks.length,
    lastUpdated: new Date().toISOString(),
  }
}

// ============================================================
// TEAM PERFORMANCE
// ============================================================

export async function getTeamPerformance(): Promise<JiraTeamPerformance> {
  const [openTasks, completedTasks, createdTasks] = await Promise.all([
    getAllOpenTasks(),
    getAllRecentlyCompletedTasks(30),
    getAllRecentlyCreatedTasks(30),
  ])

  const allTasks = [...openTasks, ...completedTasks]
  const uniqueUsers = extractUniqueAssignees(allTasks)

  // Calculate workload balance
  const taskCounts = uniqueUsers.map(user => 
    allTasks.filter(t => t.fields.assignee?.accountId === user.accountId).length
  )
  const workloadBalance = calculateWorkloadBalance(taskCounts)

  // Daily metrics for last 30 days
  const dailyMetrics = generateDailyMetrics(completedTasks, createdTasks, 30)

  // Calculate team velocity
  const teamVelocity = Math.round(completedTasks.length / 4 * 10) / 10

  return {
    totalMembers: uniqueUsers.length,
    totalTasks: allTasks.length,
    totalOpenTasks: openTasks.length,
    totalCompletedTasks: completedTasks.length,
    avgCompletionRate: Math.round((completedTasks.length / allTasks.length) * 100),
    teamVelocity,
    workloadBalance,
    avgResolutionTime: calculateAvgResolutionTime(completedTasks),
    totalTimeSpent: 0, // Would need enhanced issues
    statusDistribution: calculateTasksByStatus(allTasks),
    projectDistribution: calculateTasksByProject(allTasks),
    priorityDistribution: calculateTasksByPriority(allTasks),
    dailyMetrics,
  }
}

function calculateWorkloadBalance(taskCounts: number[]): number {
  if (taskCounts.length === 0) return 100
  
  const mean = taskCounts.reduce((sum, c) => sum + c, 0) / taskCounts.length
  const variance = taskCounts.reduce((sum, c) => sum + Math.pow(c - mean, 2), 0) / taskCounts.length
  const stdDev = Math.sqrt(variance)
  
  const cv = mean > 0 ? (stdDev / mean) * 100 : 0
  return Math.max(0, Math.min(100, Math.round(100 - cv)))
}

function generateDailyMetrics(
  completedTasks: JiraIssue[], 
  createdTasks: JiraIssue[],
  days: number
): JiraTeamPerformance['dailyMetrics'] {
  const metrics: JiraTeamPerformance['dailyMetrics'] = []
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date()
    date.setDate(date.getDate() - i)
    const dateStr = date.toISOString().split('T')[0]
    
    const completed = completedTasks.filter(t => 
      t.fields.resolutiondate?.startsWith(dateStr)
    ).length
    
    const created = createdTasks.filter(t => 
      t.fields.created.startsWith(dateStr)
    ).length
    
    metrics.push({
      date: dateStr,
      completed,
      created,
      timeSpent: 0, // Would need worklog data
    })
  }
  
  return metrics
}

// ============================================================
// UTILITY EXPORTS
// ============================================================

export function isJiraConfigured(): boolean {
  return true
}

export { jiraConfig } from './config'
